// feedUtils.js
'use strict';
import LRU from 'lru-cache';

import {
  FeedNameType,
  SUPPORTED_FEED_QUERIES,
} from '../resource/feedConstants.js';

const cache = new LRU({ max: 256 });

/**
 * Sort by alphabet ascending
 * @param {string} a - a item
 * @param {string} b - b item
 */
export const sortByAlphabetAscending = (a, b) => (b > a ? -1 : 1);

/**
 * Get sorted queries
 * @param {array} {filters} - feed filters.
 * @param {array} {sorts} - feed sorts.
 * @param {number} {page} - page number.
 * @param {number} {limit} - page size.
 * @param {number} {timestamp} - timestamp.
 * @param {number|undefined} {noRedirect} - no redirect.
 * @return {array} Return sorted queries
 */
export const getSortedQueries = ({
  filters = [],
  sorts = [],
  page,
  limit,
  timestamp,
  noRedirect,
} = {}) => {
  const querySet = new Set([
    ...filters.map(filter => `filters=${filter}`),
    ...sorts.map(sort => `sorting=${sort}`),
  ]);
  if (page != null) {
    querySet.add(`page=${page}`);
  }
  if (limit != null) {
    querySet.add(`limit=${limit}`);
  }
  if (timestamp != null) {
    querySet.add(`_=${timestamp}`);
  }
  if (noRedirect != null) {
    querySet.add(`no-redirect=${noRedirect}`);
  }
  const queries = [...querySet].sort(sortByAlphabetAscending);
  return queries;
};

/**
 * Get sorted feed names
 * @param {string} {feed} - feed name or feed id.
 * @return {array} Return sorted feed name
 */
export const getSortedFeedNames = ({ feed = '' } = {}) => {
  const feedNameSet = new Set();
  feed
    .split('&')
    .filter(s => s)
    .forEach(feedName => feedNameSet.add(feedName));
  const names = [...feedNameSet].sort(sortByAlphabetAscending);
  return names;
};

/**
 * Parse query options.
 * @param {array} {options} - selected query options.
 * @return {object} Return parsed option object.
 */
export const parseQueryOptions = ({ options = [] } = {}) => {
  const feedNameSet = new Set();
  const filterSet = new Set();
  const sortSet = new Set();
  options.forEach(option => {
    const [feedNameString = '', queryString = ''] = option.split('?');
    feedNameString
      .split('&')
      .filter(feedName => feedName)
      .forEach(feedName => feedNameSet.add(feedName));
    queryString
      .split('&')
      .filter(query => query)
      .forEach(query => {
        const [key, value] = query.split('=');
        switch (key) {
          case 'filters': {
            filterSet.add(value);
            break;
          }
          case 'sorting': {
            sortSet.add(value);
            break;
          }
        }
      });
  });
  const intersectedFeedNames = [...feedNameSet].sort(sortByAlphabetAscending);
  const joinedFeedName = intersectedFeedNames.join('&');
  const sortedFilters = [...filterSet].sort(sortByAlphabetAscending);
  const sortedSorts = [...sortSet].sort(sortByAlphabetAscending);
  const queries = [
    ...sortedFilters.map(filter => `filters=${filter}`),
    ...sortedSorts.map(sort => `sorting=${sort}`),
  ].sort(sortByAlphabetAscending);
  const result = {
    intersectedFeedName: joinedFeedName,
    intersectedFeedNames,
    filters: sortedFilters,
    sorts: sortedSorts,
    feedNameWithQuery: `${joinedFeedName}${
      queries.length ? `?${queries.join('&')}` : ''
    }`,
  };
  return result;
};

/**
 * Get feed type from feed.
 * @param {string} {feedName} - feed name.
 * @return {FeedNameType} Return feed type.
 */
export const getFeedNameType = ({ feedName = '' } = {}) =>
  /^(user|tinder|discover)_/i.test(feedName)
    ? FeedNameType.USERS
    : /^(hashtag)_/i.test(feedName)
    ? FeedNameType.HASHTAGS
    : FeedNameType.MESSAGES;

/**
 * Get category from category and location.search sorting and filters
 * @param {string} {category} - feed category.
 * @param {string} {locationSearch} - location search.
 * @return {string} The feed category.
 */
export const getFeedCategory = ({ category = '', search = '' } = {}) => {
  const searchParams = new URLSearchParams(search);
  [...searchParams.keys()].forEach(key => {
    if (!SUPPORTED_FEED_QUERIES.includes(key)) {
      searchParams.delete(key);
    }
  });
  const searchString = searchParams.toString();
  const cacheKey = `${category}:${searchString}`;
  let result = cache.get(cacheKey);
  if (!result) {
    result = decodeURIComponent(
      `${category}${searchString ? `?${searchString}` : ''}`
    );
    cache.set(cacheKey, result);
  }
  return result;
};
